import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { CHART_CONFIG, REPORT_COLORS } from '../../constants';
import type { RestorationPoint } from '../../../types/report';
import './RestorationCurve.css';

/**
 * Cumulative restoration. A single series, so the title names it and no legend
 * box is needed. Bins tell you how outages are distributed; this answers the
 * question a reader actually has — will my power be back by dinner?
 */
export function RestorationCurve({
  points,
  medianHours,
}: {
  points: RestorationPoint[];
  medianHours: number;
}) {
  const data = points.map((p) => ({ ...p, label: `${p.hours}h` }));

  return (
    <figure className="RestorationCurve">
      <figcaption className="RestorationCurve-caption">
        Share of community outages restored within a given time.
      </figcaption>
      <div className="RestorationCurve-plot">
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid stroke={CHART_CONFIG.gridStroke} vertical={false} />
            <XAxis dataKey="label" stroke={CHART_CONFIG.axisStroke} tickLine={false} fontSize={12} />
            <YAxis stroke={CHART_CONFIG.axisStroke} tickLine={false} axisLine={false}
                   fontSize={12} unit="%" domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} allowDataOverflow />
            <Tooltip
              contentStyle={CHART_CONFIG.tooltipStyle}
              cursor={{ stroke: CHART_CONFIG.axisStroke }}
              formatter={(v: number) => [`${v}% restored`, '']}
              labelFormatter={(l) => `Within ${l}`}
            />
            <ReferenceLine y={50} stroke={CHART_CONFIG.axisStroke} strokeDasharray="3 3"
                           label={{ value: `median ${medianHours}h`, position: 'insideTopRight',
                                    fill: CHART_CONFIG.axisStroke, fontSize: 11 }} />
            <Line type="monotone" dataKey="pctRestored" stroke={REPORT_COLORS.magnitude}
                  strokeWidth={2} dot={{ r: 3 }} isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <details className="RestorationCurve-table">
        <summary>View as table</summary>
        <table>
          <thead><tr><th scope="col">Within</th><th scope="col">Restored</th></tr></thead>
          <tbody>
            {data.map((p) => (
              <tr key={p.hours}>
                <td>{p.hours} hours</td>
                <td>{p.pctRestored}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </figure>
  );
}
